// Pure logic for the City Visits screen: turning the form's free-text inputs
// into the API's fields, and API visits into history rows. No React, no I/O.
// (The server validates everything again; this gives an immediate message.)
// Every visit must carry the latitude and longitude where it was recorded —
// they are what the coverage map plots.
import { formatDate, formatDateRange } from "../../utils/tspFormat";

// The ids a journey's page links here with (positive integers only; anything
// else is ignored, never sent): ?journey=&city=&return=journey ("Visit Now") and
// ?journey=&edit=&return=journey ("Update Visit").
export function readVisitLinkParams(search) {
  const params = new URLSearchParams(search || "");
  const id = (key) => (/^[1-9]\d{0,9}$/.test(params.get(key) || "") ? params.get(key) : "");
  return { journeyId: id("journey"), cityId: id("city"), editVisitId: id("edit"), returnTo: params.get("return") === "journey" ? "journey" : "" };
}

// A journey's route cities (GET /api/tsp/routes/:journeyId) -> the form's City
// options. A city with a recorded visit is listed but not selectable, with its
// status as on the journey page: "Completed", or "In Progress" for the current
// city. Only cities not yet visited can be picked.
const ROUTE_CITY_STATUS = { visited: "Completed", current: "In Progress" };
export function toRouteCityOptions(cities = []) {
  return cities.map((city) => ({
    id: String(city.location_id),
    label: city.label,
    status: ROUTE_CITY_STATUS[city.visit_state] || "",
    stateName: city.state_name,
    districtName: city.district_name,
    cityName: city.city_name,
    areas: city.areas || [],
  }));
}

// "Fort, Market" -> ["Fort", "Market"] (blank pieces dropped).
export function parseAreasInput(text) {
  return (text || "")
    .split(",")
    .map((area) => area.trim())
    .filter(Boolean);
}

// A plain decimal number, as the server accepts it ("19.2960", "-73.05"): no
// exponents, hex, units or words — so what is shown is exactly what is saved.
const DECIMAL = /^[+-]?\d{1,3}(\.\d{1,15})?$/;

// One coordinate typed or captured into the form -> a number, or { error }.
// Both coordinates are REQUIRED on every visit.
export function parseCoordinate(text, { label, min, max }) {
  const trimmed = String(text ?? "").trim();
  if (!trimmed) return { error: `${label} is required. Use “Use my location”, or enter it.` };
  if (!DECIMAL.test(trimmed)) return { error: `${label} must be a decimal number, for example ${label === "Latitude" ? "19.2960" : "73.0580"}.` };
  const value = Number(trimmed);
  if (value < min || value > max) return { error: `${label} must be between ${min} and ${max}.` };
  return { value };
}

export function parseCoordinates(latitudeText, longitudeText) {
  const latitude = parseCoordinate(latitudeText, { label: "Latitude", min: -90, max: 90 });
  if (latitude.error) return { error: latitude.error, field: "latitude" };
  const longitude = parseCoordinate(longitudeText, { label: "Longitude", min: -180, max: 180 });
  if (longitude.error) return { error: longitude.error, field: "longitude" };
  return { latitude: latitude.value, longitude: longitude.value };
}

// The device's current local time as "HH:MM" — what the Time field is
// pre-filled with when the form opens.
export function currentTimeOfDay(now = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

// Is an ISO date inside the journey's start..end dates? (Either end may be unknown.)
export function isWithinJourney(date, { startDate, endDate } = {}) {
  return (!startDate || date >= startDate) && (!endDate || date <= endDate);
}

// The visit's own fields from a form draft, or { error, field } for the first
// problem. Every field is required except Areas Visited and Remarks (an empty
// Areas Visited saves no areas), and the date must fall within
// the journey's own dates (`range` = { startDate, endDate }). Shared by Add and
// Edit, and by Add Journey's per-city Visit Info (before the journey exists).
export function validateVisitFields(draft, range) {
  if (!draft.date) return { error: "Enter the visit date.", field: "date" };
  if (!isWithinJourney(draft.date, range)) return { error: `Choose a date within the journey (${formatDateRange(range.startDate, range.endDate)}).`, field: "date" };
  if (!draft.time) return { error: "Enter the visit time.", field: "time" };
  const areas = parseAreasInput(draft.areasVisited);
  const gps = parseCoordinates(draft.latitude, draft.longitude);
  if (gps.error) return { error: gps.error, field: gps.field };
  return { fields: { visit_date: draft.date, visit_time: draft.time, areas_visited: areas, gps_latitude: gps.latitude, gps_longitude: gps.longitude } };
}

// The form draft -> the create-visit request, or { error } for the first problem.
export function buildVisitPayload(draft, journeyId, range = {}) {
  if (!journeyId || !draft.locationId) return { error: "Select a journey and a city." };
  const checked = validateVisitFields(draft, range);
  if (checked.error) return checked;

  const payload = { journey_id: Number(journeyId), location_id: Number(draft.locationId), ...checked.fields };
  if (draft.remarks.trim()) payload.remarks = draft.remarks.trim();
  return { payload };
}

// The edit form draft -> the update-visit request (journey and city never change;
// an emptied Remarks clears it), or { error } for the first problem.
export function buildVisitUpdatePayload(draft, range = {}) {
  const checked = validateVisitFields(draft, range);
  if (checked.error) return checked;
  return { payload: { ...checked.fields, remarks: draft.remarks.trim() } };
}

// A recorded visit -> the edit form's draft.
export function visitToDraft(visit) {
  return {
    date: visit.visit_date || "",
    time: (visit.visit_time || "").slice(0, 5),
    areasVisited: (visit.areas_visited || []).join(", "),
    latitude: visit.gps_latitude === null || visit.gps_latitude === undefined ? "" : String(visit.gps_latitude),
    longitude: visit.gps_longitude === null || visit.gps_longitude === undefined ? "" : String(visit.gps_longitude),
    remarks: visit.remarks || "",
  };
}

// An API visit -> a row of the Visit History table.
export function toHistoryRow(visit) {
  return {
    id: visit.id,
    date: formatDate(visit.visit_date),
    time: visit.visit_time || "—",
    journey: visit.journey.journey_name || "—",
    city: visit.location.label || visit.location.city_name || "—",
    areasVisited: visit.areas_visited,
    gps: visit.gps_latitude === null || visit.gps_longitude === null ? "" : `${visit.gps_latitude}, ${visit.gps_longitude}`,
    remarks: visit.remarks || "",
    // A visit can be edited only while its journey is in progress (the server's rule).
    editable: visit.journey.status === "IN_PROGRESS",
  };
}
